import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";
import { BookingSummary } from "../../../../../types/bookingCreation";
import {
  selectAssetGroupById,
  selectLocationById,
} from "../../../../../redux/slice/booking/bookingSlice";
import { atStation } from "../../../../../util/stationTime";
import { useBookingTimeZone } from "../../../../../util/locationTimeZone";
import BookingStatus from "./BookingStatus";
import { getBookingStatusType, getEventType } from "../../../../../util";
import { isOvertime } from "../../../../../util/overtime";
import { ServiceTypeSelectorOption } from "../../../../../types/order";
import { i18n } from "../../../../../i18n/i18n";
import { useGetUsageLogQuery } from "../../../../../api/usageLog/usageLogApiSlice";
import { deriveUsageStatus } from "../../../../useOrder/generic/useUsageLifecycle";
import { useUseCaseFlow } from "../../../../useOrder/generic/useUseCaseFlow";
import { serviceLabelPath } from "../../../../orders/serviceLabel";
import { resolveStayTimes } from "./stayTimes";
import { isRoomFamily, spaceOf } from "../../../../../util/useCases";

/**
 * What a venue ticket names under "Gebuchter Service".
 *
 * #2352: the value used to be the event type, with the service name only
 * along for the ride — so N-00000283, created without a Veranstaltungstyp
 * (optional in the admin create form), left the label with nothing after it.
 * The name is the use case and is always there; the event type refines it when
 * the booking carries one.
 *
 * The name itself comes from `serviceLabelPath`, the map the acceptance screen
 * and the Buchungen tab already use, so all three call it the same thing.
 */
const venueServiceName = (
  t: TFunction,
  service: string,
  eventType: string | undefined,
  language: string
): string => {
  // The ticket's own use case (a venue, or a sports hall on the same flow).
  const name = t(serviceLabelPath(service));
  if (!eventType) return name;
  return `${name} - ${getEventType(eventType, language)}`;
};

type BookingSummaryCardProps = {
  bookingSummaryData: BookingSummary;
  amount?: number;
  serviceType: string;
};
function BookingSummaryCard({
  bookingSummaryData,
  amount = 1,
  serviceType,
}: BookingSummaryCardProps) {
  const { t } = useTranslation();

  // Defensive extraction of first item (most code assumed index 0)
  const firstItem = bookingSummaryData?.items?.[0];
  const groupId = firstItem?.groupId;
  const startDate = firstItem?.fromDate;
  const endDate = firstItem?.toDate;
  const eventType = firstItem?.metadata?.eventType;

  const bookedItem = useSelector((state: RootState) =>
    selectAssetGroupById(state, groupId as any)
  );
  const location = useSelector((state: RootState) =>
    selectLocationById(state, bookingSummaryData?.locationId)
  );
  // This booking's own location clock (#3219) — never the selection.
  const timeZone = useBookingTimeZone(bookingSummaryData);
  // Selected as the stored object, resolved outside the selector: returning a
  // freshly built `{checkIn, checkOut}` from `useSelector` is a new reference
  // on every store update and re-renders this card for changes that cannot
  // affect it.
  const tenantConfig = useSelector(
    (state: RootState) => state.tenantConfig.config
  );
  const stayTimes = useMemo(() => resolveStayTimes(tenantConfig), [tenantConfig]);

  const currentLanguage = i18n.language;
  const recipientData = bookingSummaryData?.recipientData;
  const service = (bookingSummaryData?.service || "").toLowerCase();
  // A stay (booked by the night) prints the house check-in/out times; an
  // event-flow ticket names the room and the service with its event type.
  const isStay = spaceOf(service)?.timeUnit === "night";
  const isEventFlow = spaceOf(serviceType)?.flow === "event";
  const hideAssignedLockerRow = isRoomFamily(service);

  // Resolve localized names (prefer names carried with booking to ensure snapshot consistency)
  const langNames = firstItem?.group?.langNames || bookedItem?.langNames;
  const bookedItemName = firstItem?.group?.name || bookedItem?.name;

  // Memoize formatted booked item name to avoid recomputation on every render
  const formattedBookedItemName = useMemo(() => {
    if (!bookedItem) return "";
    if (!currentLanguage) return "";

    const localizedTitle = langNames?.[currentLanguage]?.title;

    switch (bookingSummaryData.service) {
      case ServiceTypeSelectorOption.P2P:
      case ServiceTypeSelectorOption.STORAGE:
        return `${amount}x ${t(
          "orders.ticket_overview.booked_ticket.locker"
        )} - ${t("orders.ticket_overview.booked_ticket.size")} ${
          localizedTitle ?? bookedItemName ?? ""
        }`.trim();
      case ServiceTypeSelectorOption.SUP: {
        const count = bookingSummaryData?.items?.length || 0;
        return `${count} x ${localizedTitle ?? bookedItemName ?? ""}`.trim();
      }
      default:
        return localizedTitle ?? bookedItemName ?? "";
    }
  }, [
    bookedItem,
    currentLanguage,
    langNames,
    bookingSummaryData,
    amount,
    bookedItemName,
    t,
  ]);
  
  // if (!bookedItem) return null; // Still guard – cannot display without asset group

  const textBoldClass =
    "text-foreground/80 font-bold leading-[20px] tracking-[0.02em] break-words";

  const renderTime = (date: any, isStart: boolean) => {
    if (!date) return "--";
    // A stay is booked by date, so its timestamps carry no meaningful time of
    // day and the house rule is printed instead. That rule is now the tenant's
    // (`stayTimes`), not the literal 15:00 / 11:00 every tenant used to show.
    if (isStay) return isStart ? stayTimes.checkIn : stayTimes.checkOut;
    // Dates and times on this card are the station's wall clock (ADR-0019
    // rule 2), not the phone's: a stay stored as 22.10. 00:00 Berlin must not
    // read 21.10. on a phone in London.
    return atStation(date, timeZone).format("LT");
  };

  // #2040: `isPaid` is the CURRENT balance; `wasPaid` is sticky history. This
  // card is where B-00000674 read "Gebucht" in green while the customer owed
  // the difference for an admin rebooking into a pricier slot.
  const bookingStatus = getBookingStatusType(
    firstItem?.rental,
    serviceType,
    firstItem,
    bookingSummaryData.wasPaid,
    bookingSummaryData.isPaid,
    // #1704: a concluded postpay contract is BOOKED even though nothing is
    // paid — without this the card reads "Reserviert" for a booking the
    // customer is entitled to use.
    bookingSummaryData.contractConcludedAt,
    bookingSummaryData.paymentStatus
  );

  // Generic check-in/checkout flow (venue/apartment): the badge derives from
  // the usage_log lifecycle (canonical state model,
  // docs/architecture/check-in-check-out-flow.md), not from the clock vs. the
  // booking window that getBookingStatusType falls back to:
  // - not checked in yet → "booked", even inside the booked window (a booking
  //   only becomes active through an actual check-in);
  // - checked in → "active", or "overtime" once past the scheduled end;
  // - checked out → "finished".
  // A never-started booking past its end keeps getBookingStatusType's value,
  // which is now `expired` (#2084). This note previously read "the
  // admin-facing expired state is a backend concern" — no longer true: the
  // status is shown to customers too, so both apps agree on the same
  // booking. A real check-out still wins, via the usageStatus branch below.
  // Gated per use case (PO, 2026-09-03) and category (ADR-0026 §B) — resolved
  // for THIS card's service and booking, not the selected one: the carousel
  // renders its neighbours too, and a tenant who runs the flow for one
  // category only keeps the legacy status on the others' cards.
  const cardFlow = useUseCaseFlow(service, bookingSummaryData);
  const isGenericVenueApartment = cardFlow !== null && isRoomFamily(service);
  const { data: usageLog } = useGetUsageLogQuery(
    { orderId: bookingSummaryData?.id ?? "" },
    { skip: !isGenericVenueApartment || !bookingSummaryData?.id }
  );
  const usageStatus = deriveUsageStatus(usageLog);
  let displayStatus = bookingStatus;
  if (isGenericVenueApartment) {
    if (usageStatus === "finished") {
      displayStatus = "finished";
    } else if (usageStatus === "active") {
      displayStatus = isOvertime(endDate, true, false) ? "overtime" : "active";
    } else if (bookingStatus === "active") {
      // In the booked window but not checked in — stay "booked".
      displayStatus = "booked";
    }
  }

  const isEquipmentRentalService =
    service === ServiceTypeSelectorOption.RENTAL ||
    service === ServiceTypeSelectorOption.SUP;
  const showActiveRunningNote =
    isEquipmentRentalService && bookingStatus === "active";

  const metadata = firstItem?.metadata;
  /*
   * The two notches are CUTOUTS — the punched half-circles of a torn-off paper
   * ticket. Each is a semicircle: flat side flush with the card's edge, arc
   * bulging inward, bordered on the ARC ONLY (`border-l-0` / `border-r-0`) in
   * the card's own `foreground/10`, so the curve reads as the ticket's outline
   * bending around a hole. Filled with the PAGE colour so the card is genuinely
   * punched through.
   *
   * Two wrong answers preceded this one, both instructive:
   *
   *   `bg-primary` — filled discs in the accent colour: dots, not holes. For
   *   segmenta, whose `--color-primary` is BLACK, that meant two black dots on
   *   every ticket, patched with a `tenantName === 'segmenta' ? 'bg-white'`
   *   special case that fixed the one tenant whose accent made the mistake
   *   obvious and left it for everyone else.
   *
   *   `bg-background` — which is the CARD's colour (249,249,249), not the
   *   screen's. The holes became invisible: the right idea applied to the wrong
   *   surface (PO, production, 2026-09-15).
   *
   * `bg-white` is the page. The app sets no background on the shell or on
   * `body`, so the page is the browser default, and there is no page-background
   * token to reach for. If one is ever introduced, these twelve notches are its
   * first consumer.
   *
   * WHY `-left-px` / `-right-px` AND NOT `left-0`. An absolutely positioned
   * child is placed against the PADDING box, i.e. just inside the card's 1px
   * border — so at `left-0` the card's own border still runs straight down
   * across the notch's flat side, and the cutout reads as a circle parked on an
   * unbroken edge. Shifting one pixel outward puts the notch's white fill over
   * that border, which is the CSS equivalent of laying a background-coloured
   * line across the flat side (PO's description, 2026-09-15). The card's edge
   * then genuinely breaks, curves, and resumes.
   */

  return (
    <div className="bg-background flex flex-col pt-8 pb-8 px-5 border border-foreground/10 relative min-h-[330px]">
      <div className="absolute -left-px top-[29%] h-[14px] w-[7px] rounded-r-full border border-l-0 border-foreground/10 bg-white" />
      <div className="absolute -right-px top-[29%] h-[14px] w-[7px] rounded-l-full border border-r-0 border-foreground/10 bg-white" />

      <div className="flex flex-row justify-end text-body2">
        <BookingStatus status={displayStatus} />
      </div>
      {showActiveRunningNote && (
        <p className="text-xs text-foreground/70 text-right mt-1">
          {t("orders.ticket_overview.status.active_running")}
        </p>
      )}

      {
        bookingSummaryData.service === 'parcel'
        ? (
            metadata?.delivery && (
              <div className="flex justify-between mt-2">
                <div className="text-left">
                  <p>{t("orders.ticket_overview.delivery_date")}</p>
                  <p className={textBoldClass}>
                    {metadata?.delivery ? atStation(metadata.delivery, timeZone).format("L") : "--"}
                  </p>
                </div>
              </div>
            )
        ) :
        (
          <div className="flex justify-between mt-2">
            <div className="text-left">
              <p>{t("orders.ticket_overview.start")}</p>
              <p className={textBoldClass}>
                {startDate ? atStation(startDate, timeZone).format("L") : "--"}
              </p>
              <p className={textBoldClass}>{renderTime(startDate, true)}</p>
            </div>
            <div className="text-right">
              <p>{t("orders.ticket_overview.end")}</p>
              <p className={textBoldClass}>
                {endDate ? atStation(endDate, timeZone).format("L") : "--"}
              </p>
              <p className={textBoldClass}>{renderTime(endDate, false)}</p>
            </div>
          </div>

        )
      }
      <div className="mt-1">
        <p>{t("orders.ticket_overview.location")}</p>
        {isEventFlow ? (
          <p className={textBoldClass}>{formattedBookedItemName}</p>
        ) : (
          <p className={textBoldClass}>{location?.name ?? "--"}</p>
        )}
      </div>

      {bookingSummaryData.service === ServiceTypeSelectorOption.P2P &&
        recipientData && (
          <div className="mt-1">
            <p>{t("orders.ticket_overview.recipient_details")}</p>
            <p className={textBoldClass}>
              {recipientData?.firstname}
              {recipientData?.lastname ? ` ${recipientData.lastname}` : ""}
            </p>
            <p className={textBoldClass}>{recipientData?.email}</p>
            <p className={textBoldClass}>{recipientData?.telephone}</p>
          </div>
        )}

      {
        bookingSummaryData.service === 'parcel'
        ?
          (
            <div className="mt-1">
              <p>
                {t("orders.ticket_overview.booked_items")}
              </p>
              <p className={textBoldClass}>{t('service_type_selector.parcel_delivery')}</p>
            </div>
          )
        :
        (
          <div className="mt-1">
            <p>
              {isStay
                ? t("orders.ticket_overview.apartment")
                : t("orders.ticket_overview.booked_items")}
            </p>
            {isEventFlow ? (
              <p className={textBoldClass}>
                {venueServiceName(t, serviceType, eventType, i18n.language)}
              </p>
            ) : (
              <p className={textBoldClass}>{formattedBookedItemName}</p>
            )}
          </div>
          
        )
      }

      <div className="mt-1">
        <p className="!text-base">
          {t("orders.ticket_overview.booking_number")}
        </p>
        <p className={textBoldClass}>{bookingSummaryData.bookingNumber}</p>
      </div>

      {!hideAssignedLockerRow && (
        <div className="mt-1">
          <p className="!text-base">
            {t("orders.ticket_overview.assigned_locker")}
          </p>
          <p className={textBoldClass}>
            {firstItem?.rental?.doors?.[0]?.door ?? "--"}
          </p>
        </div>
      )}
    </div>
  );
}

export default BookingSummaryCard;
